import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectAdobeLinkName,
  selectAdobeStatus,
  selectAllAdobeData,
} from '@/store/selectors/adobeAnalysisSelectors'
import { clearTrackedNavigationLink, trackPageInfo } from '@/store/slices/adobeAnalyticSlice'
import { PAGE_DEPENDENCIES, TRACK_DATA } from '@/libs/constants/adobeAnalytics'
import { isValidArray } from '@/libs/utils/array'
import { compareObjects, isEmptyObject, isObjectChecker } from '@/libs/utils/object'

import defaultDigitalData from '../digitalData'

const useAdobeAnalysis = (trackData, pageInfo) => {
  const dispatch = useAppDispatch()
  const adobeTrackedData = useAppSelector(selectAllAdobeData)
  const adobeTrackedStatus = useAppSelector(selectAdobeStatus)
  const adobeLinkName = useAppSelector(selectAdobeLinkName)

  const [trackSections, setTrackSections] = useState([])

  useEffect(() => {
    if (trackData) {
      setTrackSections(isValidArray(trackData) ? trackData : [trackData])
    }
  }, [trackData])

  const [pageInfoData, setPageInfoData] = useState(pageInfo)
  const [executable, setIsExecutable] = useState(false)

  useEffect(() => {
    if (isObjectChecker(pageInfoData)) {
      setTimeout(() => {
        dispatch(trackPageInfo(pageInfoData))
      }, 500)
    }
  }, [dispatch, pageInfoData])

  const dependencies = useMemo(() => {
    const defaultDependencies = [TRACK_DATA.PAGEINFO]

    const concatenatedDependencies = trackSections.reduce(
      (dependenciesList, trackSection) => {
        if (PAGE_DEPENDENCIES[trackSection]) {
          return dependenciesList.concat(PAGE_DEPENDENCIES[trackSection])
        }
        return dependenciesList
      },
      defaultDependencies,
    )
    // to do: check and remove the duplicated dependency items
    return concatenatedDependencies
  }, [trackSections])

  const onCheckDependencies = useCallback(() => {
    const hasDependencies = dependencies.filter((section) =>
      !adobeTrackedStatus[section]
    ).length > 0

    setIsExecutable(!hasDependencies)
  }, [dependencies, adobeTrackedStatus])

  const triggerPageTracking = useCallback(() => {
    const isEqualPage = globalThis.digitalData?.page?.pageInfo?.pageName
      === adobeTrackedData?.page?.pageInfo?.pageName

    if (executable) {
      if (!isEmptyObject(adobeTrackedData) && !isEqualPage) {
        globalThis.digitalData = adobeTrackedData

        dispatch(clearTrackedNavigationLink())
      }

      if (
        isEqualPage
        && !isEmptyObject(adobeTrackedData)
        && !compareObjects(globalThis.digitalData, adobeTrackedData)
      ) {
        // reset the pageName property with the defaul data
        globalThis.digitalData = defaultDigitalData

        // re-trigger the current tracked data
        setTimeout(() => {
          globalThis.digitalData = adobeTrackedData
        }, 1000)

        dispatch(clearTrackedNavigationLink())
      }
    }
  }, [dispatch, executable, adobeTrackedData])

  useEffect(() => {
    onCheckDependencies()
  }, [onCheckDependencies])

  useEffect(() => {
    triggerPageTracking()
  }, [triggerPageTracking])

  useEffect(() => {
    if (adobeLinkName) {
      globalThis.digitalData = {
        ...globalThis.digitalData,
        link: {
          ...globalThis.digitalData?.link,
          linkName: adobeLinkName,
        },
      }
    }
  }, [adobeLinkName])

  return {
    triggerPageTracking,
    setPageInfoData,
  }
}

export default useAdobeAnalysis
